import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Search, Trash2, ArrowLeft, X, Phone, Mail, Tag as TagIcon, Check, Upload, Download, History, GitMerge } from "lucide-react";
import { TagChip } from "@/components/TagChip";
import CustomerInsights from "@/components/CustomerInsights";
import { PageRefreshButton } from "@/components/PageRefreshButton";
import { DetailsPendingBadge } from "@/components/DetailsPendingBadge";
import CustomerImportWizard, { downloadCustomerExport } from "@/components/CustomerImportWizard";
import CustomerImportHistory from "@/components/CustomerImportHistory";
import CustomersSpreadsheet from "@/pages/CustomersSpreadsheet";

const VIEW_STORAGE_KEY = "jb_customers_view_tab";
function loadView() {
  try {
    const v = localStorage.getItem(VIEW_STORAGE_KEY);
    return v === "spreadsheet" ? "spreadsheet" : "list";
  } catch {
    return "list";
  }
}

function PageShell({ title, kicker, action, children }) {
  return (
    <div className="page" data-testid="customers-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-[0.3em] text-accent">// {kicker}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight mt-2">{title}</h1>
        </div>
        {action}
      </div>
      <div className="mt-6">{children}</div>
    </div>
  );
}

export function CustomersList() {
  const [items, setItems] = useState([]);
  // Phase P.32 — persist the search text (24h TTL).
  const _lsQ = (() => {
    try {
      const raw = localStorage.getItem("jbk:customers:filters");
      if (!raw) return "";
      const blob = JSON.parse(raw);
      if (!blob?.savedAt || Date.now() - Number(blob.savedAt) > 24 * 60 * 60 * 1000) {
        localStorage.removeItem("jbk:customers:filters");
        return "";
      }
      return blob.filters?.q || "";
    } catch { return ""; }
  })();
  const [q, setQ] = useState(_lsQ);
  useEffect(() => {
    try {
      localStorage.setItem("jbk:customers:filters", JSON.stringify({
        savedAt: Date.now(), filters: { q },
      }));
    } catch { /* ignore */ }
  }, [q]);
  const [loading, setLoading] = useState(true);
  const [importOpen, setImportOpen] = useState(false);
  // Phase 44 — track prior-imports count for the badge + history dialog.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyCount, setHistoryCount] = useState(0);
  // Phase 57 — power-user spreadsheet tab. Defaults to "list" so existing
  // users aren't surprised; persisted in localStorage across reloads.
  const [view, setView] = useState(loadView);
  useEffect(() => {
    try { localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* ignore */ }
  }, [view]);

  // Phase 59.3 — show one-off (Private Cash) customers toggle. Persists
  // across reloads so the manager doesn't keep re-flipping it.
  const [showOneOff, setShowOneOff] = useState(() => {
    try { return localStorage.getItem("jb_customers_show_oneoff") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("jb_customers_show_oneoff", showOneOff ? "1" : "0"); } catch { /* ignore */ }
  }, [showOneOff]);

  const load = async (search = "") => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (showOneOff) params.include_one_off = true;
      const { data } = await api.get("/customers", { params });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  // Phase 44 — lightweight count probe. Re-runs after a successful
  // import so the badge updates in place.
  const loadHistoryCount = async () => {
    try {
      const { data } = await api.get("/customers/import-history", { params: { limit: 1 } });
      // The endpoint returns `count` for the current page; for the badge we
      // only need "any" / "zero" / a small N — refetch with bigger limit
      // when ≥1 is found so the badge shows the right number up to 50.
      if ((data?.items || []).length > 0) {
        const { data: full } = await api.get("/customers/import-history", { params: { limit: 50 } });
        setHistoryCount((full?.items || []).length);
      } else {
        setHistoryCount(0);
      }
    } catch {
      setHistoryCount(0);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(q); loadHistoryCount(); }, [showOneOff]);

  return (
    <PageShell
      title="Customers"
      kicker="Manage"
      action={
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="gap-2" data-testid="export-customers-button">
                <Download className="h-4 w-4" /> Export
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" data-testid="export-customers-menu">
              <DropdownMenuItem onClick={() => downloadCustomerExport("xlsx")} data-testid="export-xlsx">
                Export as Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => downloadCustomerExport("csv")} data-testid="export-csv">
                Export as CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            variant="outline"
            className="gap-2"
            onClick={() => setImportOpen(true)}
            data-testid="import-customers-button"
          >
            <Upload className="h-4 w-4" /> Import
          </Button>
          {historyCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 relative"
              onClick={() => setHistoryOpen(true)}
              data-testid="import-history-button"
              title="View customer import history"
            >
              <History className="h-4 w-4" />
              <span className="hidden sm:inline">History</span>
              <span
                className="ml-1 inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-accent text-accent-foreground"
                data-testid="import-history-count-badge"
              >
                {historyCount}
              </span>
            </Button>
          )}
          <Link to="/customers/duplicates">
            <Button variant="outline" className="gap-2" data-testid="find-duplicates-button">
              <GitMerge className="h-4 w-4" /> Find duplicates
            </Button>
          </Link>
          <Link to="/customers/new">
            <Button className="btn-cta gap-2" data-testid="add-customer-button">
              <Plus className="h-4 w-4" /> New customer
            </Button>
          </Link>
          <PageRefreshButton onRefresh={() => load(q)} testId="page-refresh-btn" />
        </div>
      }
    >
      <div className="mb-4 inline-flex rounded-[8px] border border-border bg-secondary p-0.5" data-testid="customers-view-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={view === "list"}
          className={`mono text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-[6px] transition-colors ${view === "list" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          onClick={() => setView("list")}
          data-testid="customers-view-list-tab"
        >
          List
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={view === "spreadsheet"}
          className={`mono text-[11px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-[6px] transition-colors ${view === "spreadsheet" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
          onClick={() => setView("spreadsheet")}
          data-testid="customers-view-spreadsheet-tab"
        >
          Spreadsheet
        </button>
      </div>

      {view === "spreadsheet" ? (
        <CustomersSpreadsheet showOneOff={showOneOff} onShowOneOffChange={setShowOneOff} />
      ) : (
        <>
          <div className="flex items-center gap-2 max-w-md mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name or phone..."
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load(q)}
            className="pl-9 h-11"
            data-testid="customers-search-input"
          />
        </div>
        <Button variant="outline" onClick={() => load(q)} data-testid="customers-search-button">
          Search
        </Button>
        {q && (
          <Button variant="ghost" onClick={() => { setQ(""); load(""); try { localStorage.removeItem("jbk:customers:filters"); } catch { /* ignore */ } }} data-testid="customers-clear-search">
            <X className="h-4 w-4" />
          </Button>
        )}
        <button
          type="button"
          className={`mono text-[11px] uppercase tracking-wider px-2.5 py-1.5 rounded-[6px] border transition-colors ${
            showOneOff
              ? "bg-violet-100 text-violet-700 border-violet-300"
              : "bg-secondary text-muted-foreground border-border hover:border-violet-300"
          }`}
          onClick={() => setShowOneOff((v) => !v)}
          data-testid="customers-show-oneoff-toggle"
          title="Show hidden one-off (Private Cash) customers"
        >
          {showOneOff ? "✓ One-offs" : "Show one-offs"}
        </button>
      </div>

      {loading ? (
        <div className="text-muted-foreground text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-card border border-dashed border-border rounded-[8px] p-10 text-center">
          <div className="text-muted-foreground text-sm">No customers yet.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((c) => (
            <Link
              key={c.id}
              to={`/customers/${c.id}`}
              className={`bg-card border rounded-[8px] p-4 transition-colors ${
                c.is_one_off
                  ? "border-violet-200 hover:border-violet-400"
                  : "border-border hover:border-accent"
              }`}
              data-testid={`customer-card-${c.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-bold text-base">{c.company_name || <span className="text-muted-foreground italic font-normal">(no company name yet)</span>}</div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {c.details_pending && <DetailsPendingBadge />}
                  {c.is_one_off && (
                    <span
                      className="mono text-[9px] uppercase tracking-widest font-bold bg-violet-100 text-violet-700 border border-violet-200 rounded-sm px-1.5 py-0.5"
                      data-testid={`customer-card-${c.id}-oneoff`}
                      title="Private cash — one-off customer"
                    >
                      1-off
                    </span>
                  )}
                </div>
              </div>
              {c.contact_name && (
                <div className="text-sm text-muted-foreground">{c.contact_name}</div>
              )}
              {c.tags?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1" data-testid={`customer-tags-${c.id}`}>
                  {c.tags.map((t) => <TagChip key={t.id} tag={t} size="xs" />)}
                </div>
              )}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {c.phones[0] && (
                  <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {c.phones[0]}</span>
                )}
                {c.emails[0]?.email && (
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {c.emails[0].email}</span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
        </>
      )}

      <CustomerImportWizard
        open={importOpen}
        onOpenChange={setImportOpen}
        onCompleted={() => { load(q); loadHistoryCount(); }}
      />
      <CustomerImportHistory
        open={historyOpen}
        onOpenChange={setHistoryOpen}
      />
    </PageShell>
  );
}

const emptyCustomer = {
  company_name: "",
  contact_name: "",
  phones: [""],
  emails: [{ email: "", receives_invoices: false }],
  notes: "",
  tag_ids: [],
  default_payment_type_code: "",
};

function TagMultiSelect({ allTags, value, onChange }) {
  const selected = new Set(value || []);
  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const selectedTags = (allTags || []).filter((t) => selected.has(t.id));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" className="w-full justify-start gap-2 min-h-11 h-auto py-2" data-testid="customer-tags-trigger">
          <TagIcon className="h-4 w-4 text-muted-foreground" />
          {selectedTags.length === 0 ? (
            <span className="text-muted-foreground">No tags</span>
          ) : (
            <span className="flex flex-wrap gap-1">
              {selectedTags.map((t) => <TagChip key={t.id} tag={t} size="xs" />)}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start" data-testid="customer-tags-popover">
        <div className="p-2 border-b border-border mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
          Pick tags
        </div>
        <div className="max-h-64 overflow-y-auto divide-y divide-border">
          {(allTags || []).length === 0 ? (
            <div className="p-3 text-sm text-muted-foreground">No tags defined. Create them in Settings.</div>
          ) : allTags.map((t) => {
            const on = selected.has(t.id);
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => toggle(t.id)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-secondary/60 text-left"
                data-testid={`customer-tag-option-${t.id}`}
              >
                <TagChip tag={t} size="xs" />
                {on && <Check className="h-4 w-4 text-accent" />}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function CustomerForm({ mode }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = mode === "edit";
  const [form, setForm] = useState(emptyCustomer);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  // Phase P.3 Item 5 — mirror the backend `details_pending` computation on
  // the form so the "Details to follow" cue lights up live as the manager
  // clears the field. Only meaningful once the customer has a phone number.
  const detailsPendingCompany = !(form.company_name || "").trim() &&
    (form.phones || []).some((p) => (p || "").trim());
  const detailsPendingEmail = !(form.emails || []).some((e) => (e?.email || "").trim()) &&
    (form.phones || []).some((p) => (p || "").trim());

  useEffect(() => {
    Promise.all([
      api.get("/customer-tags").catch(() => ({ data: [] })),
      api.get("/settings/payment-types").catch(() => ({ data: [] })),
      isEdit ? api.get(`/customers/${id}`) : Promise.resolve(null),
    ]).then(([tagRes, ptRes, custRes]) => {
      setAllTags(tagRes.data || []);
      setPaymentTypes(ptRes.data || []);
      if (custRes?.data) {
        const c = custRes.data;
        setForm({
          company_name: c.company_name || "",
          contact_name: c.contact_name || "",
          phones: c.phones?.length ? c.phones : [""],
          emails: c.emails?.length ? c.emails : [{ email: "", receives_invoices: false }],
          notes: c.notes || "",
          tag_ids: c.tag_ids || [],
          default_payment_type_code: c.default_payment_type_code || "",
        });
      }
    }).catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const updatePhone = (i, v) => setForm((f) => {
    const phones = [...f.phones]; phones[i] = v; return { ...f, phones };
  });
  const addPhone = () => setForm((f) => ({ ...f, phones: [...f.phones, ""] }));
  const removePhone = (i) => setForm((f) => ({ ...f, phones: f.phones.filter((_, idx) => idx !== i) }));

  const updateEmail = (i, key, v) => setForm((f) => {
    const emails = [...f.emails]; emails[i] = { ...emails[i], [key]: v }; return { ...f, emails };
  });
  const addEmail = () => setForm((f) => ({ ...f, emails: [...f.emails, { email: "", receives_invoices: false }] }));
  const removeEmail = (i) => setForm((f) => ({ ...f, emails: f.emails.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    // Phase P.3 Item 5 — accept phone-only, defer company/email. Only reject
    // if we have absolutely no way to identify the customer.
    const anyPhone = (form.phones || []).some((p) => (p || "").trim());
    const anyCompany = (form.company_name || "").trim();
    if (!anyPhone && !anyCompany) {
      toast.error("Add at least a phone number or company name");
      return;
    }
    setSubmitting(true);
    const payload = {
      company_name: form.company_name,
      contact_name: form.contact_name,
      notes: form.notes,
      phones: form.phones.map((p) => p.trim()).filter(Boolean),
      emails: form.emails.filter((em) => em.email.trim()).map((em) => ({ ...em, email: em.email.trim() })),
      tag_ids: form.tag_ids,
      default_payment_type_code: form.default_payment_type_code || null,
    };
    try {
      if (isEdit) {
        await api.put(`/customers/${id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer created");
      }
      navigate("/customers");
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async () => {
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      navigate("/customers");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (loading) return <div className="page text-muted-foreground text-sm">Loading…</div>;

  return (
    <PageShell
      kicker={isEdit ? "Edit" : "Create"}
      title={isEdit ? "Edit customer" : "New customer"}
      action={
        <Link to="/customers" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="back-customers">
          <ArrowLeft className="h-4 w-4" /> Back
        </Link>
      }
    >
      <form onSubmit={submit} className="bg-card border border-border rounded-[8px] p-6 space-y-6 max-w-3xl" data-testid="customer-form">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">
              Company name
              <span className="text-muted-foreground font-normal ml-1">(optional — fill in later)</span>
            </Label>
            <Input
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              placeholder="Leave blank if unknown for now"
              className={detailsPendingCompany ? "border-red-300 focus-visible:ring-red-400" : ""}
              data-testid="customer-company-input"
            />
            {detailsPendingCompany && (
              <div className="text-[11px] text-red-700 mono uppercase tracking-wider font-bold" data-testid="customer-company-pending-hint">
                Details to follow — chase up when customer replies
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Contact name</Label>
            <Input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} data-testid="customer-contact-input" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Tags</Label>
            <TagMultiSelect
              allTags={allTags}
              value={form.tag_ids}
              onChange={(v) => setForm({ ...form, tag_ids: v })}
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider font-semibold">Default payment type</Label>
            <Select
              value={form.default_payment_type_code || "_system"}
              onValueChange={(v) => setForm({ ...form, default_payment_type_code: v === "_system" ? "" : v })}
            >
              <SelectTrigger data-testid="customer-default-payment-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_system" data-testid="customer-default-payment-option-system">Use system default</SelectItem>
                {paymentTypes.map((p) => (
                  <SelectItem key={p.id} value={p.code} data-testid={`customer-default-payment-option-${p.code}`}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider font-semibold">Phones</Label>
            <Button type="button" variant="outline" size="sm" onClick={addPhone} data-testid="add-phone-button">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          {form.phones.map((p, i) => (
            <div key={i} className="flex gap-2">
              <Input
                value={p}
                onChange={(e) => updatePhone(i, e.target.value)}
                placeholder={i === 0 ? "Primary phone" : "Additional phone"}
                data-testid={`customer-phone-input-${i}`}
              />
              {form.phones.length > 1 && (
                <Button type="button" variant="ghost" size="icon" onClick={() => removePhone(i)} data-testid={`remove-phone-${i}`}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider font-semibold">Emails</Label>
            <Button type="button" variant="outline" size="sm" onClick={addEmail} data-testid="add-email-button">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
          {form.emails.map((em, i) => (
            <div key={i} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <Input
                type="email"
                value={em.email}
                onChange={(e) => updateEmail(i, "email", e.target.value)}
                placeholder="email@example.com"
                className="flex-1"
                data-testid={`customer-email-input-${i}`}
              />
              <label className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  checked={em.receives_invoices}
                  onCheckedChange={(v) => updateEmail(i, "receives_invoices", !!v)}
                  data-testid={`customer-email-invoices-${i}`}
                />
                Receives invoices
              </label>
              {form.emails.length > 1 && (
                <Button type="button" variant="ghost" size="icon" onClick={() => removeEmail(i)} data-testid={`remove-email-${i}`}>
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider font-semibold">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={4} data-testid="customer-notes-input" />
        </div>

        <div className="flex flex-wrap gap-2 justify-between border-t border-border pt-4">
          <div className="flex gap-2">
            <Button type="submit" disabled={submitting} className="btn-cta" data-testid="customer-save-button">
              {submitting ? "Saving..." : isEdit ? "Save changes" : "Create customer"}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate("/customers")} data-testid="customer-cancel-button">
              Cancel
            </Button>
          </div>
          {isEdit && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="destructive" data-testid="customer-delete-button">
                  <Trash2 className="h-4 w-4 mr-1" /> Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete this customer?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This action cannot be undone. The customer record will be permanently removed.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="customer-delete-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={onDelete} data-testid="customer-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </form>

      {isEdit && <CustomerInsights customerId={id} />}
    </PageShell>
  );
}
